import { z } from 'zod';
import { ArtifactKind, BuildPriority, BuildStatus, ExecutionMode, Platform, TriggerSource } from '@hangar/shared';
import { str, nstr, nint } from './schemaPrimitives';
import { Actor, AppSummary, CheckResult, CheckStatus, ClientExecutionPublic, EnvironmentSummary, ErrorInfo, GitRef, ProfileSummary, ProviderAccountSummary, UserSummary, Warning } from './schemasCommon';
import { MissingEnvRequirement } from './schemasEnv';

export const Build = z.object({
  id: str,
  number: z.number(),
  ref: str,
  buildRequestId: str.optional(),
  projectId: str,
  app: AppSummary,
  platform: Platform,
  profile: ProfileSummary,
  environment: EnvironmentSummary,
  providerAccount: ProviderAccountSummary,
  executionMode: ExecutionMode.catch('hangar_runner'),
  status: BuildStatus,
  statusReason: nstr,
  slotHeld: z.boolean().optional(),
  priority: BuildPriority.catch('normal'),
  priorityValue: nint,
  queuePosition: nint,
  git: GitRef,
  versionName: nstr,
  buildNumber: nint,
  fingerprint: nstr,
  runtimeVersion: nstr,
  updateChannel: nstr,
  envSnapshotId: nstr,
  inputs: z.record(str, z.unknown()).optional(),
  clearCache: z.boolean().optional(),
  autoSubmit: z.object({ enabled: z.boolean(), track: nstr }).nullish(),
  toolchain: z.record(str, str).optional(),
  estimatedCostUsd: nint,
  actualCostUsd: nint,
  provider: z.object({ buildId: nstr, buildUrl: nstr, statusRaw: nstr, meta: z.record(str, z.unknown()).optional() }).optional(),
  parentBuildId: nstr,
  redeployMode: nstr,
  attempt: z.number().default(1),
  supersededById: nstr,
  releaseId: nstr,
  approvalId: nstr,
  source: TriggerSource.catch('ui'),
  actor: Actor,
  ciContext: z.object({ provider: nstr, workspace: nstr, repository: nstr, pipelineUuid: nstr, buildNumber: nint, url: nstr, actor: nstr }).nullish(),
  queuedAt: nstr,
  dispatchedAt: nstr,
  startedAt: nstr,
  finishedAt: nstr,
  deadlineAt: nstr,
  durationSeconds: nint,
  cancelRequestedAt: nstr,
  cancelRequestedBy: UserSummary.nullish(),
  error: ErrorInfo.nullish(),
  clientExecution: ClientExecutionPublic.optional(),
  createdAt: str,
  updatedAt: str,
});
export type Build = z.infer<typeof Build>;

export const BuildList = z.object({ data: z.array(Build), nextCursor: z.string().nullable() });

export const BuildSummary = z.object({ id: str, number: z.number(), ref: str, appId: str, appSlug: str.optional(), platform: Platform, environment: str, profile: str.optional(), status: BuildStatus });

export const BuildEvent = z.object({
  id: str,
  kind: str,
  fromStatus: nstr,
  toStatus: nstr,
  source: str.optional(),
  actorId: nstr,
  providerEventAt: nstr,
  payload: z.record(str, z.unknown()).default({}),
  createdAt: str,
});
export type BuildEvent = z.infer<typeof BuildEvent>;
export const BuildEventList = z.object({ data: z.array(BuildEvent), nextCursor: z.string().nullable() });

export const BuildCheck = z.object({ id: str, key: str, status: CheckStatus.catch('warning'), blocking: z.boolean(), summary: nstr, details: z.record(str, z.unknown()).optional(), createdAt: str });
export type BuildCheck = z.infer<typeof BuildCheck>;
export const BuildCheckList = z.object({ data: z.array(BuildCheck) });

export const Artifact = z.object({
  id: str,
  buildId: str,
  kind: ArtifactKind.catch('metadata'),
  dataClass: str.optional(),
  filename: str,
  storageClass: str.optional(),
  pinned: z.boolean().default(false),
  legalHold: z.boolean().default(false),
  deletedAt: nstr,
  sizeBytes: nint,
  sha256: nstr,
  contentType: nstr,
  signing: z.object({ sha1: nstr, sha256: nstr, subject: nstr, validUntil: nstr }).nullish(),
  appMetadata: z.record(str, z.unknown()).nullish(),
  installable: z.boolean().optional(),
  mirroredAt: nstr,
  retentionUntil: nstr,
  createdAt: str,
});
export type Artifact = z.infer<typeof Artifact>;
export const ArtifactList = z.object({ data: z.array(Artifact) });
/**
 * `GET /artifacts/{id}/download?redirect=false`. `requiresAuth: false` = a presigned S3 URL (no bearer token may go
 * there); `true` = the API's own download route (local Hangar), fetched with the bearer token. Absent = presigned.
 */
export const PresignedUrl = z.object({ url: str, expiresAt: nstr, requiresAuth: z.boolean().default(false) });
export type PresignedUrl = z.infer<typeof PresignedUrl>;

export const BuildPlan = z.object({
  platform: Platform,
  profile: ProfileSummary,
  environment: EnvironmentSummary,
  providerAccount: ProviderAccountSummary,
  executionMode: ExecutionMode.catch('hangar_runner'),
  providerConfig: z.record(str, z.unknown()).optional(),
  versionName: nstr,
  buildNumber: nint,
  envContentHash: str.optional(),
  envMissing: z.array(MissingEnvRequirement).default([]),
  estimatedCostUsd: nint,
  approvalRequired: z.boolean().optional(),
  approvalReason: nstr,
  queue: z.object({ position: z.number().optional(), estimatedStartAt: nstr }).optional(),
  autoSubmit: z.object({ track: str.optional(), allowed: z.boolean().optional(), approvalRequired: z.boolean().optional() }).nullish(),
  checks: z.array(CheckResult).default([]),
  blocked: z.boolean(),
});
export type BuildPlan = z.infer<typeof BuildPlan>;
export const BuildRequestResult = z.object({ id: z.string().nullish(), dryRun: z.boolean(), builds: z.array(Build), warnings: z.array(Warning).default([]), plan: z.array(BuildPlan).default([]) });
export type BuildRequestResult = z.infer<typeof BuildRequestResult>;

export const BuildRequest = z.object({ id: str, app: AppSummary, source: TriggerSource.catch('ui'), actor: Actor, git: GitRef.optional(), inputs: z.record(str, z.unknown()).optional(), createdAt: str, builds: z.array(BuildSummary) });
export type BuildRequest = z.infer<typeof BuildRequest>;
